import { useTranslations } from "next-intl";
import { Link } from "react-router-dom";
import { CheckboxWithValidation } from "@/components/Inputs/CheckboxWithValidation";
import { legalDocument, legalDocumentPath } from "@/lib/legal/documents";

/**
 * The SEPARATE consent to the data processing agreement and its annexes.
 *
 * Deliberately its own checkbox rather than another clause bolted onto the
 * terms sentence. The DPA is a distinct contract with distinct annexes (A–C,
 * the TOMs being annex C), and § 3(2) of the SaaS terms says it is pointed out
 * separately. One tick covering four documents is not that, and it also makes
 * the audit row unable to say which of them the person actually saw.
 *
 * Both links open the in-app `/legal/:doc` route rather than an outside URL.
 * That route is the single place that knows whether a document is published —
 * see `lib/legal/documents.ts`. Linking straight to `veltric.app/avv` would
 * reproduce exactly the bug this replaces: as of 2026-09-06 that path answers
 * 404.
 *
 * The click-to-toggle handler ignores clicks that land on a link, for the same
 * reason as the terms sentence: opening a document must never be mistaken for
 * accepting it.
 *
 * Shown only where a COMPANY is created (self-serve signup, the invite form
 * with the invite section off): an invitee joins an organisation whose DPA
 * already stands, so that lane asks for the terms box alone (W9 1-S ⚑A1).
 *
 * `isInvalid` = "the person tried to proceed without this tick" — it drives
 * the visual state, `aria-invalid`, `aria-describedby` and the error line in
 * one go, exactly like `TermsConsentCheckbox`; the form may pass a `ref` to
 * focus the box.
 */
export function DpaConsentCheckbox({
  isChecked,
  isInvalid,
  onChangeAction,
  testId = "auth-register-dpa",
  ref,
}: {
  isChecked: boolean;
  isInvalid: boolean;
  onChangeAction: (checked: boolean) => void;
  /** Distinct per surface so a test can tell the invite form from the signup form. */
  testId?: string;
  ref?: React.Ref<HTMLDivElement>;
}) {
  const t = useTranslations("legal");
  const labelId = `${testId}-label`;
  const errorId = `${testId}-error`;
  const dpa = legalDocument("dpa");
  const toms = legalDocument("toms");

  return (
    <div>
      <div className="flex items-start">
        <CheckboxWithValidation
          ref={ref}
          isChecked={isChecked}
          isInvalid={isInvalid}
          testId={testId}
          alignStart
          ariaLabelledBy={labelId}
          ariaRequired
          ariaInvalid={isInvalid}
          ariaDescribedBy={isInvalid ? errorId : undefined}
          onCheckChangeAction={onChangeAction}
        />
        <span
          id={labelId}
          className="body-2-regular"
          onClick={(e) => {
            if ((e.target as HTMLElement).closest("a")) return;
            onChangeAction(!isChecked);
          }}
        >
          {t("dpaConsent1")}{" "}
          <Link
            to={legalDocumentPath(dpa.key)}
            target="_blank"
            rel="noopener noreferrer"
            className="body-2-bold underline"
          >
            {t("dpa")}
          </Link>{" "}
          {t("dpaConsent2")}{" "}
          <Link
            to={legalDocumentPath(toms.key)}
            target="_blank"
            rel="noopener noreferrer"
            className="body-2-bold underline"
          >
            {t("toms")}
          </Link>
        </span>
      </div>
      {isInvalid && (
        <p id={errorId} role="alert" className="mt-1 body-3-regular font-error">
          {t("consentRequired")}
        </p>
      )}
    </div>
  );
}
